/**
 * ChatWidget - Virtual CISO Assistant
 *
 * A slide-in chat panel that provides AI-powered assistance for GRC platform users.
 * Features:
 * - Context-aware responses based on current page
 * - Conversation history management
 * - CISO persona for compliance and security guidance
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import {
  X,
  Send,
  Bot,
  User,
  Trash2,
  MessageSquare,
  RotateCcw,
  Loader2,
  ChevronDown,
} from 'lucide-react'
import { api } from '../lib/api'

// Simple markdown renderer for chat messages
function formatChatMessage(content: string): React.ReactNode {
  const lines = content.split('\n')
  const elements: React.ReactNode[] = []
  let currentList: string[] = []
  let listType: 'ul' | 'ol' | null = null

  const flushList = () => {
    if (currentList.length > 0) {
      if (listType === 'ol') {
        elements.push(
          <ol key={`list-${elements.length}`} className="list-decimal list-inside space-y-0.5 my-1">
            {currentList.map((item, i) => (
              <li key={i}>{formatInlineMarkdown(item)}</li>
            ))}
          </ol>
        )
      } else {
        elements.push(
          <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-0.5 my-1">
            {currentList.map((item, i) => (
              <li key={i}>{formatInlineMarkdown(item)}</li>
            ))}
          </ul>
        )
      }
      currentList = []
      listType = null
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Bullet list items
    if (line.match(/^[-*]\s/)) {
      if (listType !== 'ul') {
        flushList()
        listType = 'ul'
      }
      currentList.push(line.replace(/^[-*]\s/, ''))
      continue
    }

    // Numbered list items
    if (line.match(/^\d+\.\s/)) {
      if (listType !== 'ol') {
        flushList()
        listType = 'ol'
      }
      currentList.push(line.replace(/^\d+\.\s/, ''))
      continue
    }

    // Non-list content
    flushList()

    // Headers (h3 style for chat)
    if (line.startsWith('### ')) {
      elements.push(
        <div key={i} className="font-semibold mt-2 mb-1">
          {formatInlineMarkdown(line.slice(4))}
        </div>
      )
    } else if (line.startsWith('## ')) {
      elements.push(
        <div key={i} className="font-semibold mt-2 mb-1">
          {formatInlineMarkdown(line.slice(3))}
        </div>
      )
    } else if (line.startsWith('# ')) {
      elements.push(
        <div key={i} className="font-bold mt-2 mb-1">
          {formatInlineMarkdown(line.slice(2))}
        </div>
      )
    } else if (line.trim() === '') {
      // Empty line = paragraph break
      elements.push(<div key={i} className="h-2" />)
    } else {
      // Regular text
      elements.push(
        <div key={i}>{formatInlineMarkdown(line)}</div>
      )
    }
  }

  flushList()

  return <>{elements}</>
}

// Format inline markdown (bold, code)
function formatInlineMarkdown(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0

  while (remaining.length > 0) {
    // Bold (**text**)
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/)
    // Code (`code`)
    const codeMatch = remaining.match(/`(.+?)`/)

    // Find which comes first
    const boldIndex = boldMatch ? boldMatch.index! : Infinity
    const codeIndex = codeMatch ? codeMatch.index! : Infinity

    if (boldIndex === Infinity && codeIndex === Infinity) {
      // No more markdown
      parts.push(remaining)
      break
    }

    if (boldIndex < codeIndex) {
      const before = remaining.slice(0, boldIndex)
      if (before) parts.push(before)
      parts.push(<strong key={key++}>{boldMatch![1]}</strong>)
      remaining = remaining.slice(boldIndex + boldMatch![0].length)
    } else {
      const before = remaining.slice(0, codeIndex)
      if (before) parts.push(before)
      parts.push(
        <code key={key++} className="bg-black/10 px-1 py-0.5 rounded text-xs font-mono">
          {codeMatch![1]}
        </code>
      )
      remaining = remaining.slice(codeIndex + codeMatch![0].length)
    }
  }

  return parts.length > 0 ? parts : text
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: number
}

interface ChatWidgetProps {
  isOpen: boolean
  onClose: () => void
}

export function ChatWidget({ isOpen, onClose }: ChatWidgetProps) {
  const location = useLocation()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null)
    }
  }, [isOpen])

  // Send message
  const handleSend = async () => {
    if (!input.trim() || isLoading) return

    const userMessage = input.trim()
    setInput('')
    setError(null)

    // Add user message optimistically
    const newUserMessage: ChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: Date.now(),
    }
    setMessages((prev) => [...prev, newUserMessage])
    setIsLoading(true)

    try {
      const response = await api.sendChatMessage({
        message: userMessage,
        conversationId: conversationId || undefined,
        currentPage: location.pathname,
      })

      if (response.success && response.data) {
        // Add assistant message
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: response.data.message,
          timestamp: Date.now(),
        }
        setMessages((prev) => [...prev, assistantMessage])
        setConversationId(response.data.conversationId)
      } else {
        setError(response.error || 'Failed to get response')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message')
    } finally {
      setIsLoading(false)
    }
  }

  // Handle Enter key (Shift+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Start new conversation
  const handleNewConversation = () => {
    setMessages([])
    setConversationId(null)
    setError(null)
    inputRef.current?.focus()
  }

  // Clear all conversations
  const handleClearAll = async () => {
    if (!confirm('Clear all chat history? This cannot be undone.')) return

    try {
      await api.clearAllChatConversations()
      handleNewConversation()
    } catch (err) {
      setError('Failed to clear conversations')
    }
  }

  // Get page title for context indicator
  const getPageTitle = (path: string): string => {
    const titles: Record<string, string> = {
      '/': 'Dashboard',
      '/dashboard': 'Dashboard',
      '/controls': 'Controls',
      '/risks': 'Risks',
      '/risk-appetite': 'Risk Appetite',
      '/kris': 'KRIs',
      '/evidence': 'Evidence',
      '/documents': 'Documents',
      '/tasks': 'Tasks',
      '/tprm': 'TPRM',
      '/scanners': 'Scanners',
      '/assets': 'Assets',
      '/dark-web': 'Dark Web',
      '/integrations': 'Integrations',
      '/users': 'Users',
      '/settings': 'Settings',
      '/mappings': 'Mappings',
      '/frameworks': 'Frameworks',
      '/reports': 'Reports',
    }
    return titles[path] || 'This Page'
  }

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Chat Panel */}
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-primary-600 to-primary-700">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-white/20 rounded-lg">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-white">Virtual CISO</h2>
              <p className="text-xs text-white/80">
                Context: {getPageTitle(location.pathname)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleNewConversation}
              className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="New conversation"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
            <button
              onClick={handleClearAll}
              className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="Clear all history"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="p-4 bg-primary-50 rounded-full mb-4">
                <Bot className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Virtual CISO Assistant
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                I can help with compliance, risk management, security best practices,
                and navigating the platform. Ask me anything!
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  'How do I add evidence to a control?',
                  'Explain risk scoring',
                  'What is SOC 2?',
                  'Help with vulnerability remediation',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setInput(suggestion)
                      inputRef.current?.focus()
                    }}
                    className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${
                    message.role === 'user' ? 'flex-row-reverse' : ''
                  }`}
                >
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      message.role === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="h-4 w-4" />
                    ) : (
                      <Bot className="h-4 w-4" />
                    )}
                  </div>
                  <div
                    className={`flex-1 max-w-[80%] ${
                      message.role === 'user' ? 'text-right' : ''
                    }`}
                  >
                    <div
                      className={`inline-block px-4 py-2 rounded-2xl ${
                        message.role === 'user'
                          ? 'bg-primary-600 text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-900 rounded-bl-md'
                      }`}
                    >
                      <div className="text-sm">
                        {message.role === 'assistant'
                          ? formatChatMessage(message.content)
                          : message.content}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 mt-1 px-1">
                      {new Date(message.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 text-gray-600">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                      <span className="text-sm text-gray-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Input Area */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask the Virtual CISO..."
                rows={1}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-12"
                style={{
                  minHeight: '44px',
                  maxHeight: '120px',
                }}
                disabled={isLoading}
              />
              {input.length > 0 && (
                <span className="absolute right-3 bottom-2 text-xs text-gray-400">
                  {input.length}/2000
                </span>
              )}
            </div>
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>,
    document.body
  )
}

// Chat Button for header
export function ChatButton() {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        title="Virtual CISO"
      >
        <MessageSquare className="h-5 w-5" />
      </button>
      <ChatWidget isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  )
}
